// Canonical module-keys registry (role-manager rework PR-2 Commit 6).
//
// Single source of truth for the term `moduleKey` across the codebase:
//   - `platform.entitlements.module_key` (the schema column)
//   - `withMiddleware({ entitlement: '<moduleKey>' })` (the gate opt)
//   - `PermissionMetadata.module` (the per-permission sidecar)
//   - `/me` response field `entitlements[].moduleKey`
//   - `useEntitlement(moduleKey)` hook arg
//   - `EntitlementsApi.check(tenantId, moduleKey)` arg
//
// `scripts/check-module-key.cjs` validates that every reference to a
// moduleKey across the codebase resolves against this list. Adding a
// new module surface (new admin app, new tenant-facing module) is a
// code change here + a coordinated migration of the affected tenants'
// `platform.entitlements` rows by platform-admin.
//
// The list is deliberately small. Modules that exist in the codebase
// but are NOT operator-facing surfaces (e.g. `platform-infra`) are
// excluded — they are always-available substrate, not subject to
// plan tier or feature toggles.
//
// `tenants` is in the list as a **substrate exception** (role-manager
// rework PR-3a per ADR-0061): no handler ever opts in with
// `entitlement: 'tenants'` (the admin surface is always-available
// substrate, like `platform-infra`), but `PERMISSION_METADATA` carries
// `module: 'tenants'` on the new `tenants.roles.*` strings to group
// them under the admin role-builder UI's "Tenants" category. The
// gate's two passes serve different purposes:
//   - entitlement opts: this list IS the entitled-modules registry
//   - PermissionMetadata.module: this list IS the UI-grouping registry
// Both are unified by membership in MODULE_KEYS; the gate just checks
// "is the value in the list," not "is the value entitled." If a future
// rework wants to enforce "entitled modules ⊆ MODULE_KEYS strictly,"
// that's a follow-up gate, not a rename of this one.
export const MODULE_KEYS = [
    'commerce',
    // DEPRECATED 2026-07-10 — split into 'pos_retail' + 'pos_fnb' so a tenant
    // can be licensed for one, the other, or both simultaneously (a resort
    // running a retail gift shop and an F&B restaurant). Never deleted (keys
    // are immutable — audit logs + JWT claims carry it forever); blocked from
    // NEW grants by the mutation engine (see entitlement-mutation.ts). See
    // docs/superpowers/specs/2026-07-10-pos-retail-fnb-coexistence-design.md.
    'pos',
    'pos_retail',
    'pos_fnb',
    'kds',
    'catalog',
    'customers',
    'workforce',
    'payroll-providers',
    'financials',
    // AP module (modules/ap) — bill entry workflow, approval rules, and
    // approval-decision routing. Registered for PERMISSION_METADATA.module
    // grouping of the ap.* permissions; entitlement wiring is a follow-up.
    'ap',
    // Fixed-assets module (modules/fixed-assets) — asset register and
    // depreciation runs. Entitlement wiring shipped in ticket #8048 (every
    // route in modules/fixed-assets/server/routes.ts carries
    // entitlement:'fixed-assets').
    'fixed-assets',
    // IRS 1099 e-filing module (modules/tax-1099-filing) — compile-readiness
    // + payee gap-filler. Registered for PERMISSION_METADATA.module grouping
    // of the tax-1099-filing.* permissions; entitlement wiring is a follow-up.
    'tax-1099-filing',
    'ar',
    'house-accounts',
    'lodging',
    'gift-cards',
    'integrations',
    'audit',
    'billing',
    'tax',
    // comms (MEGA-3) — operator-facing comms surface (templates, suppression
    // list, send-log read, outbound send). Carried on PERMISSION_METADATA.module
    // for the role-builder UI grouping; not yet wired as an entitlement opt.
    'comms',
    // Substrate exception per PR-3a — not entitled, but the role-builder
    // UI groups tenants.* permissions under "Tenants."
    'tenants',
    // Settings UX shell areas (S-Boot-26 PR-8e) — not yet entitled modules
    // but registered as module-key values so PERMISSION_METADATA.module
    // validates and the role-builder UI can group settings.* permissions.
    'reservations',
    'loyalty',
    // POS v2 §11 memberships/loyalty backend (modules/memberships). Registered
    // for PERMISSION_METADATA.module grouping of the memberships.* permissions;
    // entitlement wiring (plan-tier gating) is a follow-up.
    'memberships',
    // Mileage expensing module (modules/mileage) — vehicle register, trip
    // log, mileage rates, and claims. Phase 1 ships the vehicle/trip CRUD
    // surface; entitlement wiring (plan-tier gating) is a follow-up.
    'mileage',
    // Budgets module (modules/budgets) — plans, versions, lines, and
    // approval workflow. Registered for PERMISSION_METADATA.module grouping
    // of the budgets.* permissions; entitlement wiring is a follow-up.
    'budgets',
    // Merchant-services module (modules/payments — merchant boarding,
    // processor credentials, settlement) — payment-processing infrastructure,
    // not a purchasable plan-tier feature (visibility: 'internal' in
    // registry.ts excludes it from every PLAN_REGISTRY tier by the plan-
    // registry.test.ts invariant). DEFAULT-ON (2026-08-02 decision,
    // entitlement-enforcement arc #7897) — the only internal+default-granted
    // entry today (billing/audit/tenants/permissions stay internal +
    // defaultGranted:false; this is a deliberate new shape, not a precedent
    // match). Every real-world POS/payments competitor (Square, Toast, Clover,
    // Lightspeed, ValorPayTech) bundles payment-processing capability into
    // the core product rather than gating it behind a subscription tier —
    // monetization is per-transaction, not per-feature-unlock. Also required
    // by 6 MANDATORY onboarding steps
    // (modules/tenants/seed/onboarding-step-templates.foundation-pos.json);
    // gating this without a grant path would 402 every tenant, including
    // brand-new signups mid-onboarding. See DEFAULT_GRANTED_MODULE_KEYS below.
    // NOTE: visibility:'internal' also makes this module non-tenant-activatable
    // (module-activation-policy.ts) -- combined with defaultGranted:true, the
    // deny path (an admin hand-writing an off/locked row) is the ONLY way to
    // revoke it; there is no self-serve toggle. Defensible, but real -- see PR body.
    'merchant-services',
    'printers',
    'taxes',
    'receipts',
    'cash-handling',
    'permissions',
    // AI assistant module (modules/assistant) — grounded read-only Q&A,
    // drafting, and write-actions. DEFAULT-ON: a tenant with no explicit
    // entitlement row is treated as entitled (full access). Operators opt
    // OUT per-tenant by setting access_mode='off' or 'locked'.
    // See DEFAULT_GRANTED_MODULE_KEYS below. (ADR-0100 amendment.)
    'assistant',
    // Branding module (modules/branding) — tenant white-label brand configuration
    // (logo, colors, typography, content) per ADR-0102. Feature-gated, flag-off by default.
    'branding',
    // Room layouts module (modules/room-layouts) — floor plan room CRUD + layout
    // versioning (Room Layouts V2). Registered for PERMISSION_METADATA.module
    // grouping of the room-layouts.* permissions; entitlement wiring (plan-tier
    // gating) is a follow-up. Feature-gated by ROOM_LAYOUTS_ENABLED, flag-off by default.
    'room-layouts',
    // Insights module (modules/insights) - governed read-only analytics
    // query substrate (AI Tools). DEFAULT-ON (2026-09-26 product decision,
    // ticket #10485: AI Tools is universal, not growth-tier -- only 2 of 25
    // staging tenants could open it). See DEFAULT_GRANTED_MODULE_KEYS below.
    'insights',
    // Support/feedback module (modules/support) — operator feedback submission,
    // curated roadmap, voting. Registered for PERMISSION_METADATA.module grouping
    // and entitlement gating. DEFAULT-ON (2026-07-08 product decision: every
    // tenant should be able to submit bug reports / feature requests with a
    // screenshot, no per-tenant allowlist step). See DEFAULT_GRANTED_MODULE_KEYS
    // below. Previously staged for a per-tenant allowlist rollout ("spec D28")
    // that was never built — no such doc/ADR exists in this repo.
    'support',
    // F&B reporting module (modules/fnb-reporting) — S12 Phase-1 revenue
    // read-model reports (ADR-0139): daypart/hourly sales, menu mix,
    // discount & comp analysis. Registered for PERMISSION_METADATA.module
    // grouping of the reports.fnb-reporting.* permissions; entitlement
    // wiring (plan-tier gating) is a follow-up.
    'fnb-reporting',
    // Dining module (modules/dining) — F&B live floor, host stand, guest
    // waitlist. Registered for PERMISSION_METADATA.module grouping of the
    // dining.* permissions and gated as a real entitlement (ticket #6421):
    // parked while scope narrows, defaultGranted: false, no tenant holds a
    // grant today. See registry.ts for the dependsOn note.
    'dining',
    // customer-billing module (modules/customer-billing) -- recurring-billing
    // subscriptions/invoices + the Plan D Prepayments reconciliation surface.
    // Registered for PERMISSION_METADATA.module grouping of the
    // customer_billing.* permissions. Entitlement-gated server-side and
    // starter-tier since 2026-10-09 (see plan-registry.ts STARTER_CORE).
    'customer-billing',
    // Appointments module (modules/appointments) — scheduling/booking kernel
    // (S7 backoffice UX). Registered for PERMISSION_METADATA.module grouping
    // of the appointments.* permissions and so its setup wizard can register
    // in module-wizard-routes.ts's WIZARD_ENTITLEMENT_KEYS (parity-tested
    // against WIZARD_DELTA_SCHEMAS). Entitlement wiring is DONE as of ticket
    // #8068 (2026-08-05): bundled into every plan tier via plan-registry.ts's
    // STARTER_CORE, enforced by the /appointments/* entitlement guard in
    // apps/root-api/src/index.ts plus per-route writeAccess, and still
    // `defaultGranted: false` so it stays individually toggleable per-tenant.
    'appointments',
    // Virtual Terminal module (modules/payments) — card-only Quick Charge /
    // Send-a-link collection surface (VT PR-2). Pilot-only per
    // virtual-terminal-CONTRACT.md's explicit gating requirement — a new,
    // dedicated ModuleKey rather than a piggyback on pos_retail/pos_fnb
    // (those gate unrelated feature sets; Decision 4, PR-2 design doc).
    // defaultGranted: false (see registry.ts) — NOT in
    // DEFAULT_GRANTED_MODULE_KEYS below.
    'virtual_terminal',
    // Customer Display module (apps/customer-apps + modules/pos/api-interface/
    // customer-display.ts) — retail Pusher/tablet pairing display (PR-5 of the
    // POS structural bug remediation initiative). A new, dedicated ModuleKey
    // rather than a piggyback on pos_retail (that gates the unrelated broader
    // retail-POS feature set; every other gated surface — pos_retail, pos_fnb,
    // dining, kds — is its own named key, so piggybacking would be the actual
    // deviation). defaultGranted: false (see registry.ts) — NOT in
    // DEFAULT_GRANTED_MODULE_KEYS below.
    'pos_customer_display',
    // SMS invoice delivery (ticket #9343) -- adds `sms` as a second delivery
    // channel for the ar.invoice_issued/ar.invoice_reminder notification
    // kinds, on top of the existing email-only send. Its own key rather than
    // reusing the parked `comms` key (ruled 2026-08-19, §10.2 of
    // docs/superpowers/plans/2026-08-19-invoice-sms-delivery.md): decoupled
    // from whatever eventually unblocks the parked marketing-comms scope,
    // and no bundling of two different products (marketing campaigns vs.
    // invoice delivery) under one toggle. defaultGranted: false (see
    // registry.ts) -- PRO_ADDS-only per plan-registry.ts.
    'sms_invoice_delivery',
    // quotes (Billing Quotes Phase 2) -- quotes API + workspace surface.
    // Registered for PERMISSION_METADATA.module grouping of quotes.* permissions,
    // matching the customer-billing precedent above -- entitlement wiring
    // (plan-tier gating) is a follow-up alongside PR-3's nav entry, not built
    // here.
    'quotes',
    // Pickup Board (modules/pos -- GET /checks/pickup, PATCH
    // /checks/:checkId/pickup-ready) -- the pickup/online check worklist.
    // PARKED 2026-09-03 (ticket #10716) while product scope narrows; revisit in
    // a few months. defaultGranted: false, so no tenant is entitled and both
    // routes 402 -- see registry.ts and
    // docs/superpowers/plans/2026-09-03-park-tab-board-pickup-board-gate.md.
    //
    // A dedicated key rather than a piggyback on pos_retail/pos_fnb (those gate
    // unrelated feature sets), matching the virtual_terminal /
    // pos_customer_display precedent for a POS sub-surface. Deliberately NOT
    // paired with a `pos_tab_board` sibling: the Tab Board's only endpoint
    // (GET /checks/pullable) is shared with the POS register's order-tabs-bar,
    // so a Tab Board key would gate nothing. Tab Board is parked at the nav +
    // route layer only.
    'pos_pickup_board',
    // Write-Ups module (modules/write-ups) -- internal PO-handoff documents
    // (cost/supplier/case-pack/shipping) auto-created on quote acceptance or
    // created manually, emailed as PDF for QuickBooks handoff (Task 7 of the
    // 2026-09-04 write-ups implementation plan). Registered for
    // PERMISSION_METADATA.module grouping of the write-ups.* permissions and
    // as a real entitlement guard on /api/v1/write-ups/*, matching the
    // quotes precedent immediately above.
    'write-ups',
    // Operator Conversations Inbox (modules/comms -- GET/POST /me/inbox/*, plus
    // the shared GET conversation-thread / POST send routes widened to accept
    // this key per docs/superpowers/plans/2026-09-05-park-inbox-entitlement-
    // gate.md). PARKED at ship time (Checkpoint 1 of that plan) while product
    // scope narrows -- defaultGranted: false, so no tenant is entitled and the
    // four inbox-only routes 402; the shared thread/send routes still work for
    // any tenant already holding `comms`. A dedicated key rather than a
    // piggyback on `comms` (comms gates unrelated marketing/campaign surfaces
    // and is itself parked) so Inbox and marketing-comms can un-park
    // independently -- matching the pos_pickup_board / virtual_terminal /
    // pos_customer_display precedent immediately above.
    'inbox',
    // Document Templates module (modules/document-templates) -- invoice/quote
    // layout template builder (ADR-0174, Session 1 of
    // docs/plans/quote-invoice-templates/02-session-1-first-working-template.md).
    // Registered for PERMISSION_METADATA.module grouping of the
    // settings.modules.document-templates.* permissions; entitlement wiring
    // (staged tenant enablement) is a follow-up, matching the inbox precedent
    // immediately above.
    'document-templates',
    // Payment Hub module (modules/payment-hub) -- unified `payment_request`
    // primitive (destination/request/credential/attempt) collecting money for
    // invoices, accounts, orders, deposits, appointments, gift-card storefront
    // checkout and general reasons through one settlement dispatcher, replacing
    // several independently-built pay-link surfaces
    // (docs/plans/root-payment-hub-handoff/00-MASTER-root-payment-hub.md, ADR-0176).
    // Registered for PERMISSION_METADATA.module grouping of the payment_hub.*
    // permissions; entitlement wiring ships with every PAYMENT_HUB_* flag off
    // by default (visibility: 'beta' in registry.ts).
    'payment_hub',
    // Regulated profile (J12, R-24 / R-23; docs/plans/2026-10-14-j12-legal-earned-fees.md) -- NOT a
    // capability: the HeyRoot-recorded classification of a tenant (law firm, dental or medical
    // covered entity, other), held in reserved `limits` keys on one internal row written only by the
    // platform-admin regulated-profile lane. Legal guards read it; H4's PHI gate will too.
    // defaultGranted: false and internal, so it is in no plan tier.
    'regulated_profile',
];
const MODULE_KEY_SET = new Set(MODULE_KEYS);
/**
 * Type-narrowing guard. Use when validating an untrusted string against
 * the canonical list — e.g. when parsing a `module_key` value from
 * `platform.entitlements` or a `useEntitlement(...)` hook arg from
 * dynamic UI state.
 */
export function isModuleKey(value) {
    return typeof value === 'string' && MODULE_KEY_SET.has(value);
}
/**
 * True for a key that is a HeyRoot-recorded platform record, not a capability toggle
 * (today only `regulated_profile`, #15687). Generic module lists, matrices and a tenant's
 * own `/me` entitlements skip these; the regulated-profile admin lane owns them. A named
 * check rather than a set: no second platform-record key exists.
 * `__tests__/platform-record-module-key.test.ts` pins it against `REGULATED_PROFILE_KEY`
 * (that constant lives in the zod-importing
 * `regulated-profile.ts`, which this zod-free module must not import).
 */
export function isPlatformRecordModuleKey(key) {
    return key === 'regulated_profile';
}
/**
 * The module keys every toggle surface offers (the module matrix, the module-set editor, the tenant module
 * picker and `/meta/module-keys`): every registered key except a platform record. One list, so a second
 * platform-record key cannot be hidden from three surfaces and shown on the fourth (#16378). A fresh array
 * on every call.
 */
export function toggleModuleKeys() {
    return MODULE_KEYS.filter((key) => !isPlatformRecordModuleKey(key));
}
/**
 * Module keys that are GRANTED BY DEFAULT: a tenant with NO explicit
 * entitlement row is treated as entitled (full access). Operators opt OUT
 * per-tenant by setting an explicit access_mode of 'off' or 'locked'.
 * (Amends ADR-0100: the assistant ships on-by-default rather than opt-in.)
 */
export const DEFAULT_GRANTED_MODULE_KEYS = new Set([
    'assistant',
    'room-layouts',
    'support',
    'merchant-services',
    // 2026-08-14 (onboarding W-4 fix, docs/plans/onboarding tier-A): 'tax' has
    // no wizard designation mapping and no module dependsOn edge, so it could
    // never be materialized as an entitlement through onboarding at all -- the
    // 'financial.tax_groups'/'financial.tax_rates' onboarding steps were
    // permanently invisible and the resolver's satisfiesDep auto-satisfied
    // catalog.items's blocking dependency on financial.tax_groups. Removed
    // from STARTER_CORE in plan-registry.ts (binding invariant: a
    // default-granted key is never also plan-applier-materialized) -- unlike
    // 'commerce' (which also carries STARTER_CORE's max_locations/max_devices
    // resource limits and could NOT make this move safely) and 'financials'
    // (a real pro-tier-monetized module), 'tax' carries no limits and no other
    // system reads its entitlement row for anything beyond the plain
    // entitled/not-entitled check, so default-granting it is a clean fix.
    'tax',
    // 2026-09-26 (ticket #10485): AI Tools is universal -- same "every tenant
    // should have it" call as 'assistant'/'support'. Removed from GROWTH_ADDS
    // in plan-registry.ts per the binding invariant above.
    'insights',
]);
//# sourceMappingURL=module-keys.js.map